import React, { useMemo } from 'react';
import { Text, TooltipTrigger } from '@spark';
import { getTaskTypeShortLabelForUsecase } from '@/features/tasks/utils/types';
import { TASK_ACTIONS } from './taskActions';

const DOT = 6;
const DOT_GAP = 3;

const statusToLabel = (status) => {
    if (status === 'completed') return 'Complete';
    if (status === 'loading') return 'In progress';
    if (status === 'warning') return 'Needs attention';
    return 'Not started';
};

const statusToDotStyle = (status, isActive) => {
    const base = {
        width: DOT,
        height: DOT,
        borderRadius: DOT / 2,
        flexShrink: 0,
        boxSizing: 'border-box',
    };
    if (status === 'completed') {
        return {
            ...base,
            background: isActive
                ? 'var(--semantic-content-brand)'
                : 'var(--semantic-feedback-info-on-surface)',
        };
    }
    if (status === 'warning') {
        return { ...base, background: 'var(--semantic-feedback-warning-on-surface)' };
    }
    if (status === 'loading') {
        return {
            ...base,
            border: '1.5px solid var(--semantic-feedback-info-surface-muted)',
            borderTopColor: isActive
                ? 'var(--semantic-content-brand)'
                : 'var(--semantic-feedback-info-on-surface)',
            background: 'transparent',
        };
    }
    return {
        ...base,
        background: isActive
            ? 'var(--primitive-neutral-400)'
            : 'var(--semantic-border-default)',
    };
};

/**
 * Tab label rendered for each objective in the task tab strip.
 * Shape:
 *
 *   [icon]  Classification : intent  [• status dot]
 *
 * The optional `hasError` red dot is used by the upload modal to flag tabs
 * that have invalid column selections.
 *
 * When `actionStatusById` is set (multi-objective task strip), four micro-dots
 * summarize the same four steps as `ActionRowList` so you can scan every
 * objective without switching tabs.
 */
const ObjectiveTabLabel = ({
    usecase,
    hasError = false,
    isActive = false,
    actionStatusById = null,
}) => {
    const tooltipText = useMemo(() => {
        if (!actionStatusById) return '';
        return TASK_ACTIONS.map(
            (action) => `${action.title}: ${statusToLabel(actionStatusById[action.id])}`
        ).join('\n');
    }, [actionStatusById]);

    if (!usecase) return null;

    const showStepStrip = Boolean(actionStatusById);
    const labelColor = isActive
        ? 'var(--semantic-content-brand)'
        : 'var(--semantic-content-secondary)';

    let stepDots = null;
    if (showStepStrip) {
        const dots = (
            <span
                role="img"
                aria-label="Step progress for this objective"
                onClick={(e) => e.stopPropagation()}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: DOT_GAP,
                    marginLeft: 2,
                }}
            >
                {TASK_ACTIONS.map((action) => {
                    const st = actionStatusById[action.id];
                    const spinning = st === 'loading';
                    return (
                        <span
                            key={action.id}
                            style={{
                                ...statusToDotStyle(st, isActive),
                                ...(spinning
                                    ? {
                                          animation:
                                              'objective-tab-dot-spin 0.75s linear infinite',
                                      }
                                    : {}),
                            }}
                        />
                    );
                })}
            </span>
        );
        stepDots = tooltipText ? (
            <TooltipTrigger text={tooltipText} placement="bottom">
                {dots}
            </TooltipTrigger>
        ) : (
            dots
        );
    }

    return (
        <Text
            as="span"
            variant="base-regular"
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
            }}
        >
            {showStepStrip ? (
                <style>
                    {
                        '@keyframes objective-tab-dot-spin { to { transform: rotate(360deg); } }'
                    }
                </style>
            ) : null}
            <span style={{ color: labelColor }}>
                {getTaskTypeShortLabelForUsecase(usecase)}:
            </span>
            <span style={{ color: labelColor }}>
                {usecase.usecase_name}
            </span>
            {stepDots}
            {hasError ? (
                <span
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: 4,
                        background: 'var(--semantic-action-danger-content-on-surface)',
                        display: 'inline-block',
                    }}
                />
            ) : null}
        </Text>
    );
};

export default ObjectiveTabLabel;
